import * as turf from '@turf/turf';
import { aqiFromPM } from "./aqiFromPM.js";
import { TimelineControl } from "./TimelineControl.js"

mapboxgl.accessToken = 'pk.eyJ1Ijoic3R1ayIsImEiOiJjbDVpc3hyMHYwODNiM2pwa2YwZjdoY2MwIn0.2Z-DYob3vtLvVHgAoVqM8Q';

const fields = [
    "sensor_index",
    "name",
    "location_type",
    "latitude",
    "longitude",
    "last_seen",
    "channel_state",
    "channel_flags",
    "confidence",
    "primary_key_a",
    "pm2.5",
    "pm2.5_10minute",
    "pm2.5_30minute",
    "pm2.5_60minute",
];

// From Purple Air, color blind friendly
const COLOR_STOPS = [
    [15, "rgb(145, 255, 127)"],
    [50, "rgb(254, 191, 12)"],
    [100, "rgb(252, 109, 11)"],
    [150, "rgb(233, 0, 6)"],
    [200, "rgb(116, 0, 133)"],
    [300, "rgb(80, 0, 17)"],
];

const MAX_SENSOR_RADIUS_ZOOM_START = 9.5;
const MAX_SENSOR_RADIUS_KM_START = 2;

const MAX_SENSOR_RADIUS_ZOOM_END = 5;
const MAX_SENSOR_RADIUS_KM_END = 20;

/**
 * @param {number} from
 * @param {number} to
 * @param {number} value
 * @returns {number}
 */
function ratio(from, to, value) {
    return Math.min(Math.max((value - from) / (to - from), 0), 1);
}

/**
 * @param {number} from
 * @param {number} to
 * @param {number} ratio
 * @returns {number}
 */
function interpolate(from, to, ratio) {
    return from + (to - from) * ratio;
}

const map = new mapboxgl.Map({
    container: 'map',
    style: 'mapbox://styles/mapbox/light-v10',
    // style: 'mapbox://styles/mapbox/cjaudgl840gn32rnrepcb9b9g',
    // style: 'mapbox://styles/mapbox/satellite-streets-v11',
    zoom: 9,
    center: [-120.15660679817029, 39.19887877631868],
    // update URL when the map is moved
    hash: true,
});

/**
 *
 * @returns {Promise<{
 *   frames: {index: number, id: string, datetime: Date, url: string}[],
 *   coordinates: [[number, number], [number, number], [number, number], [number, number]]
 * }>}
 */
async function getSmokeDetails() {
    const res = await fetch('/api/aqi');
    if (!res.ok) {
        throw new Error(`AirFire forecast request failed with ${res.status} ${res.statusText}`);
    }

    const data = /** @type {AirFireImageSet} */(await res.json());
    if (!data.images?.length || data.bounding_box?.sw?.length !== 2 || data.bounding_box?.ne?.length !== 2) {
        throw new Error('AirFire forecast response is missing images or map bounds');
    }

    const [west, south] = data.bounding_box.sw;
    const [east, north] = data.bounding_box.ne;
    const tileUrl = data.url.replace(/\/$/, '');

    const frames = data.images.map((image, index) => {
        const time = image.n.replace(/\.png$/i, '');
        // AirFire forecast hours are UTC, but its metadata omits the timezone suffix.
        const datetime = new Date(`${image.dt}Z`);
        if (Number.isNaN(datetime.getTime())) {
            throw new Error(`AirFire returned an invalid forecast time: ${image.dt}`);
        }

        return {
            index,
            id: 'smoke',
            datetime,
            url: `${tileUrl}?run=current&time=${encodeURIComponent(time)}`
        };
    });

    return {
        frames,
        coordinates: [
            [west, north],
            [east, north],
            [east, south],
            [west, south],
        ]
    };
}

/**
 * @param {string} deg
 * @param {string} min
 * @param {string} sec
 * @returns {number}
 */
function toCoord(deg, min, sec) {
    return parseInt(deg, 10) + (parseInt(min, 10) / 60) + (parseInt(sec, 10) / 3600);
}

/**
 * @returns {Promise<FireFeatures>}
 */
async function getFireData() {
    const res = await fetch("/api/inciweb");
    const data = /** @type {InciwebResponse} */(await res.json());

    return {
        type: 'FeatureCollection',
        features: data.filter(marker => marker.type === "Wildfire").map(marker => {
            return {
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [-toCoord(marker.long_deg, marker.long_min, marker.long_sec), toCoord(marker.lat_deg, marker.lat_min, marker.lat_sec)]
                },
                properties: {
                    name: marker.title,
                    type: marker.type,
                    updated: marker.changed,
                    size: parseInt(marker.size, 10),
                    url: `https://inciweb.nwcg.gov/incident-information/${marker.urlPath}`,
                    contained: marker.field_percent_of_perimeter, // percent
                }
            }
        })
    };
}

/**
 *
 * @param {import("mapbox-gl").LngLatBounds} bounds
 * @returns
 */
async function getPurpleData(bounds) {
    const req = await fetch(`https://api.purpleair.com/v1/sensors?fields=${fields.join(",")}&max_age=86400&nwlat=${bounds.getNorthWest().lat}&selat=${bounds.getSouthEast().lat}&nwlng=${bounds.getNorthWest().lng}&selng=${bounds.getSouthEast().lng}`, {
        "headers": {
            "X-API-Key": "348EED7F-02BE-11ED-8561-42010A800005",
        },
        "method": "GET",
        "mode": "cors"
    });
    const res = await req;
    const sensors = /** @type {PurpleAirResponse} */(await res.json());

    const fieldIndexes = sensors.fields.reduce((/** @type {{[key: string]: number}} */obj, value, index) => {
        obj[value] = index;
        return obj;
    }, {});

    /** @type {PurpleAirResponse["getField"]} */
    sensors.getField = function (sensor, name) {
        // @ts-expect-error
        return sensor[fieldIndexes[name]];
    };

    // Only show outdoor sensors with a confidence greater than 90%
    sensors.data = sensors.data.filter(sensor =>
        sensors.getField(sensor, "location_type") === 0 && /** @type {number} */(sensors.getField(sensor, "confidence")) > 90);

    return sensors;
}

/**
 * @param {PurpleAirResponse} sensors
 * @returns {AqiFeatures}
 */
function generateSensorFeatures(sensors) {
    return {
        type: 'FeatureCollection',
        features: sensors.data.map(sensor => {
            /** @type {AqiFeature} */
            return {
                // feature for Mapbox DC
                'type': 'Feature',
                'geometry': {
                    'type': 'Point',
                    'coordinates': [
                        sensors.getField(sensor, "longitude"), sensors.getField(sensor, "latitude")
                    ]
                },
                'properties': {
                    name: sensors.getField(sensor, "name"),
                    sensorIndex: sensors.getField(sensor, "sensor_index"),
                    value: aqiFromPM(sensors.getField(sensor, "pm2.5")),
                    value10: aqiFromPM(sensors.getField(sensor, "pm2.5_10minute")),
                    value30: aqiFromPM(sensors.getField(sensor, "pm2.5_30minute")),
                    value60: aqiFromPM(sensors.getField(sensor, "pm2.5_60minute")),
                }
            }
        })
    };
}

const mapLoaded = new Promise(resolve => {
    map.on('load', resolve);
});

Promise.all([getPurpleData(map.getBounds()), getFireData(), mapLoaded]).then(([sensors, fires]) => {
    let sensorFeatures = generateSensorFeatures(sensors);

    map.addSource('sensors', {
        type: 'geojson',
        data: sensorFeatures,
        'attribution':
            'Air quality from <a target="_blank" rel="noopener" href="https://www2.purpleair.com/">PurpleAir</a>'
    });

    const currentFires = fires.features.filter(fire => {
        // something like
        // 1 month 3 weeks ago
        // 1 week 1 day ago
        // 4 hours 12 minutes ago
        // so exclude anything that mentions weeks or months
        const updated = fire.properties.updated;
        return !(updated.includes("week") || updated.includes("weeks") || updated.includes("month") || updated.includes("months"));
    });

    map.addSource('fires', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: currentFires }
    });

    map.addLayer({
        id: "fires",
        type: "circle",
        source: "fires",
        paint: {
            "circle-radius": 5,
            "circle-color": "rgba(255, 50, 0, 1)",
        }
    }, "road-label");

    map.addLayer({
        id: "sensors",
        type: "circle",
        source: "sensors",
        paint: {
            "circle-radius": 2,
            "circle-color": {
                property: 'value',
                stops: COLOR_STOPS
            },
        }
    }, "fires");

    map.addLayer({
        id: "sensors-text",
        type: "symbol",
        source: "sensors",
        layout: {
            "text-field": "{value}",
            "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Regular"],
            "text-size": 16,
            "text-anchor": "center",
            "text-radial-offset": 0.4,
        },
        paint: {
            "text-halo-color": {
                property: 'value',
                stops: COLOR_STOPS
            },
            "text-color": [
                'case',
                ['>=', ['get', 'value'], 100],
                "rgba(255,255,255,0.95)",
                "rgba(0,0,0,0.5)",
            ],
            "text-halo-width": 1,
        }
    }, "settlement-label");

    map.addLayer({
        id: "fires-text",
        type: "symbol",
        source: "fires",
        layout: {
            "text-field": "{name}",
            "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Regular"],
            "text-size": 12,
            "text-variable-anchor": ["left", "right", "top", "bottom", "top-left", "top-right", "bottom-left", "bottom-right"],
            "text-radial-offset": 0.4,
        },
        paint: {
            "text-halo-color": "rgba(255,255,255,0.9)",
            "text-color": "rgba(0,0,0,0.9)",
            "text-halo-width": 1,
        }
    });

    const v = calculateVoronoi(sensorFeatures, map.getBounds().toArray(), map.getZoom());
    addVoronoiLayer(map, v);

    getSmokeDetails().then(({frames, coordinates}) => {
        const now = new Date();
        const initialFrame = frames.reduce((previous, current) =>
            Math.abs(current.datetime.getTime() - now.getTime()) < Math.abs(previous.datetime.getTime() - now.getTime())
                ? current
                : previous
        );

        map.addSource(initialFrame.id, {
            'type': 'image',
            'url': initialFrame.url,
            'coordinates': coordinates
        });
        map.addLayer({
            'id': initialFrame.id,
            'type': 'raster',
            'source': initialFrame.id,
            'paint': {
                'raster-opacity': 0,
                'raster-opacity-transition': { duration: 0 }
            },
            layout: {
                visibility: "none"
            }
        }, "voronoiLayer");
        timelineControl.update({smoke: frames, smokeError: undefined});
    }).catch(error => {
        console.error('Unable to load AirFire smoke forecast', error);
        timelineControl.update({smokeError: 'Smoke forecast is temporarily unavailable.'});
    });


    let previousBounds = map.getBounds().toArray();
    map.on('click', function (e) {
        let features = map.queryRenderedFeatures(e.point, { layers: ['fires', 'fires-text', 'voronoiLayer'] });
        if (features.length) {
            // @ts-expect-error
            handlers[features[0].source](features[0], e);
        }
    });

    const handlers = {
        /** @type {(feature: FireFeature, e: mapboxgl.MapMouseEvent) => void} */
        fires: function (feature, e) {
            const coordinates = canonicalizeCoordinates(e.lngLat, feature.geometry.coordinates);

            // name: marker.name,
            // type: marker.type,
            // updated: marker.updated, // TODO convert
            // size: parseInt(marker.size.replace(",", "").replace(" Acres", ""), 10),
            // url: marker.url,
            // contained: marker.contained, // percent

            const description = `<strong><a target="_blank" href="https://inciweb.nwcg.gov${escape(feature.properties.url)}">${escape(feature.properties.name)}</a></strong><br/>
            ${feature.properties.size} acres<br/>
            ${feature.properties.contained}% contained<br/>
            Updated ${feature.properties.updated}`;

            e.preventDefault();

            new mapboxgl.Popup()
                // @ts-expect-error
                .setLngLat(coordinates)
                .setHTML(description)
                .addTo(map);
        },
        /** @type {(feature: AqiFeature, e: mapboxgl.EventData) => void} */
        voronoiData: (feature, e) => {
            // @ts-expect-error
            const description = `<strong>${feature.properties.value}:</strong> <a target="_blank" href="https://www.purpleair.com/map?lat=${feature.geometry.coordinates[0][0][1]}&lng=${feature.geometry.coordinates[0][0][0]}&zoom=11&show=${feature.properties.sensorIndex}">${escape(feature.properties.name)}</a><br/>
            <progress value="${feature.properties.value60}" max="400" style="width: 50px"></progress> 1 hour: ${feature.properties.value60}<br/>
            <progress value="${feature.properties.value30}" max="400" style="width: 50px"></progress> 30 mins: ${feature.properties.value30}<br/>
            <progress value="${feature.properties.value10}" max="400" style="width: 50px"></progress> 10 mins: ${feature.properties.value10}<br/>
            <progress value="${feature.properties.value}" max="400" style="width: 50px"></progress> now: ${feature.properties.value}`;

            new mapboxgl.Popup()
                .setLngLat(e.lngLat)
                .setHTML(description)
                .addTo(map);
        }
    };

    map.on("idle", () => {
        const newBounds = map.getBounds().toArray();
        if (
            newBounds[0][0] === previousBounds[0][0] &&
            newBounds[0][1] === previousBounds[0][1] &&
            newBounds[1][0] === previousBounds[1][0] &&
            newBounds[1][1] === previousBounds[1][1]
        ) {
            return;
        }

        previousBounds = newBounds;

        // Redrawing before new data leads to a misleading flash of outdated contents. Instead just wait
        // const v = generateVoronoi(sensorFeatures, newBounds);
        // map.getSource('voronoiData').setData(v);

        // TODO cancel/ignore this request if the bounds change
        getPurpleData(map.getBounds()).then(sensors => {
            sensorFeatures = generateSensorFeatures(sensors);
            /** @type {mapboxgl.GeoJSONSource} */(map.getSource('sensors')).setData(sensorFeatures);

            const v = calculateVoronoi(sensorFeatures, newBounds, map.getZoom());
            /** @type {mapboxgl.GeoJSONSource} */(map.getSource('voronoiData')).setData(v);
        });
    });
});

// Ensure that if the map is zoomed out such that multiple copies of the feature are visible, the
// popup appears over the copy being pointed to.

/**
 *
 * @param {mapboxgl.LngLat} lngLat
 * @param {number[]} coordinates
 * @returns
 */
function canonicalizeCoordinates(lngLat, coordinates) {
    coordinates = coordinates.slice();
    while (Math.abs(lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += lngLat.lng > coordinates[0] ? 360 : -360;
    }
    return coordinates;
}

/**
 * @param {string} value
 * @returns {string}
 */
function escape(value) {
    return value.replace("<", "&lt;").replace(">", "&gt;");
}

/**
 *
 * @param {AqiFeatures} collection
 * @param {number[][]} mapBounds
 * @param {number} mapZoom
 * @returns
 */
function calculateVoronoi(collection, mapBounds, mapZoom) {
    /** @type {[number, number, number, number]} */
    const voronoiExtent = [mapBounds[0][0], mapBounds[0][1], mapBounds[1][0], mapBounds[1][1]];
    const options = {
        bbox: voronoiExtent
    };
    const voronoiPolygons = turf.voronoi(collection, options);

    /** @type {AqiFeatures} */
    const geojsonPolygon = {
        "type": "FeatureCollection",
        "features": []
    };

    const features = geojsonPolygon.features;

    const maxRadius = calculateMaxRadius(mapZoom);

    for (var i = 0; i < voronoiPolygons.features.length; i++) {
        if (voronoiPolygons.features[i] != null) {
            /** @type {AqiFeature} */
            const newFeature = {
                "type": "Feature",
                "properties": collection.features[i].properties,
                // This just draws the Voronoi polygon:
                // "geometry": voronoiPolygons.features[i].geometry
                // Limit the radius of the polygon to a circle around the center
                "geometry": turf.intersect(
                    voronoiPolygons.features[i],
                    turf.circle(collection.features[i], maxRadius, { units: "kilometers", steps: 20 })
                ).geometry
            }

            features.push(newFeature);
        }

    }

    return geojsonPolygon;
}

/**
 * @param {number} mapZoom
 */
function calculateMaxRadius(mapZoom) {
    const zoomRatio = ratio(MAX_SENSOR_RADIUS_ZOOM_START, MAX_SENSOR_RADIUS_ZOOM_END, mapZoom);
    return interpolate(MAX_SENSOR_RADIUS_KM_START, MAX_SENSOR_RADIUS_KM_END, zoomRatio);
}

/**
 *
 * @param {mapboxgl.Map} map
 * @param {AqiFeatures} data
 */
function addVoronoiLayer(map, data) {
    map.addSource('voronoiData', {
        type: 'geojson',
        data: data
    });

    map.addLayer({
        'id': 'voronoiLayer',
        'type': 'fill',
        'source': 'voronoiData',
        'layout': {},
        'paint': {
            'fill-color': {
                property: 'value',
                stops: COLOR_STOPS
            },
            "fill-opacity": 0.4,
        },
    }, 'hillshade');
};

map.addControl(
    new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        mapboxgl: mapboxgl
    }),
    'top-left'
);
map.addControl(new mapboxgl.NavigationControl({ visualizePitch: false }));
map.addControl(new mapboxgl.GeolocateControl({
    positionOptions: {
        enableHighAccuracy: true
    },
}));

map.addControl(new mapboxgl.ScaleControl({ unit: 'imperial' }), "top-left");

// create style element:
var style = document.createElement('style');
style.innerText = `
.mapboxgl-ctrl-bottom-left { right: 0; }
`;
document.getElementsByTagName('head')[0].appendChild(style);

const timelineControl = new TimelineControl({
    aqiLayers: ["voronoiLayer", "sensors-text", "sensors"]
});
map.addControl(timelineControl, "bottom-left");

/** @type {any} */(window).map = map;
